import { map } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { HttpService } from "../../../common/services/http.service";
import { ServiceLocator } from "../../../common/services/service-locator.service";
import { Transaction } from "../models/transaction.model";
import { User } from "../models/user.model";
import { RechargesReportParameters } from "../../recharges/services/recharges.service";

@Injectable()
export class UsersService {
  constructor(private http: HttpService) {}

  getUsers(
    merchantId: number,
    page: number,
    size: number,
    dataBag: any,
    filterParams: string,
  ): Observable<any> {
    const http = ServiceLocator.injector.get(HttpService);
    return http.get(
      `wallet/v1/merchants/${merchantId}/users/?page=${page}&size=${size}${filterParams}`,
      dataBag,
    );
  }

  getUsersCount(
    merchantId: number,
    dataBag: any,
    filterParams: string = "",
  ): Observable<number> {
    const http = ServiceLocator.injector.get(HttpService);
    return http.get(
      `wallet/v1/merchants/${merchantId}/users/count${filterParams}`,
      dataBag,
    );
  }

  getUserWallets(merchantId: number, userId: number): Observable<any> {
    const http = ServiceLocator.injector.get(HttpService);
    return http.get(
      `wallet/v1/merchants/${merchantId}/user/${userId}/wallets/`,
    );
  }

  getAllUsers(merchantId: number, filter?: any): Observable<any> {
    return this.http.get(`wallet/v1/merchants/${merchantId}/users/?${filter}`);
  }

  getAllUsersWithWallet(merchantId: number): Observable<number[]> {
    return this.http.get(
      `wallet/v1/merchants/${merchantId}/users-with-wallets/`,
    );
  }

  sendForDiscount(
    merchantId: number,
    discountPercentage: number,
    discountText: string,
    data: any,
  ): Observable<any> {
    const json = {
      discountPercentage: discountPercentage,
      discountText: discountText,
      walletIds: data,
    };
    return this.http.post(
      "wallet/v1/merchants/" + merchantId + "/users/",
      JSON.stringify(json),
    );
  }

  getTransactions(
    merchantId: number,
    page: number,
    size: number,
    dataBag: any,
    filterParams: string,
  ): Observable<Transaction[]> {
    const http = ServiceLocator.injector.get(HttpService);
    return http
      .get(
        `wallet/v1/merchants/${merchantId}/accounts/${dataBag.walletId}/transactions?page=${page}&size=${size}&from=${dataBag.dateFrom}&to=${dataBag.dateTo}${filterParams}`,
      )
      .pipe(map((response) => response.transactions));
  }

  getTransactionsCount(
    merchantId: number,
    dataBag: any,
    filterParams: string = "",
  ): Observable<number> {
    const http = ServiceLocator.injector.get(HttpService);
    return http.get(
      `wallet/v1/merchants/${merchantId}/accounts/${dataBag.walletId}/transactions/count?from=${dataBag.dateFrom}&to=${dataBag.dateTo}${filterParams}`,
    );
  }

  lockUser(userId: number, value: number) {
    return this.http.post(
      "wallet/v1/user/" + userId + "/enable/" + (value === 1).toString(),
      "",
    );
  }

  unlockWallet(walletId: number) {
    return this.http.put("wallet/v1/accounts/" + walletId + "/unlock", "");
  }

  editBalance(merchantId: number, walletId: number, balance: any) {
    return this.http.put(
      `/wallet/v1/merchants/${merchantId}/accounts/${walletId}/${balance}`,
      "",
    );
  }

  refund(amount: number, text: string, walletId: number) {
    return this.http.post(
      "wallet/v1/merchants/" + walletId + "/recharge/credit",
      { amount: amount, text: text },
    );
  }

  refundTransaction(
    amount: number,
    text: string,
    walletId: number,
    transactionDate: any,
  ) {
    return this.http.post(
      "wallet/v1/merchants/" + walletId + "/refund/credit",
      { amount: amount, text: text, transactionDate: transactionDate },
    );
  }

  getNotifications(
    merchantId: number,
    page: number,
    size: number,
    dataBag: any,
    filterParams: string,
  ): Observable<any> {
    const http = ServiceLocator.injector.get(HttpService);
    return http
      .get(
        `wallet/v1/merchants/${merchantId}/notification/list?page=${page}&size=${size}${filterParams}`,
      )
      .pipe(map((response) => response.notifications));
  }

  getNotificationsCount(
    merchantId: number,
    dataBag: any,
    filterParams: string = "",
  ): Observable<number> {
    const http = ServiceLocator.injector.get(HttpService);
    return http.get(
      `wallet/v1/merchants/${merchantId}/notification/list/count${filterParams}`,
    );
  }

  getUnreadNotificationsCount(merchantId: number): Observable<number> {
    return this.http.get(
      `wallet/v1/merchants/${merchantId}/notification/list/count?read=0`,
    );
  }

  readNotification(merchantId: number, notificationId: number) {
    return this.http.get(
      `wallet/v1/merchants/${merchantId}/notification/${notificationId}`,
    );
  }

  replyToMessage(merchantId: number, notificationId: number, message: string) {
    return this.http.post(
      `wallet/v1/merchants/${merchantId}/reply/notification/${notificationId}`,
      { message: message },
    );
  }

  sendMessage(
    merchantId: number,
    title: string,
    message: string,
    userIds: any,
    sendAll: any,
  ): Observable<any> {
    return this.http.post(
      "wallet/v1/accounts/broadcast/message/" + merchantId,
      JSON.stringify({
        title: title,
        message: message,
        userIds: userIds,
        sendAll: sendAll,
      }),
    );
  }

  sendMessageMachineGroups(
    merchantId: number,
    title: string,
    message: string,
    machineIds: number[],
    sendAll: any,
  ): Observable<any> {
    return this.http.post(
      `wallet/v1/merchants/${merchantId}/machine/groups/broadcast/message`,
      JSON.stringify({
        title: title,
        message: message,
        machineGroupIds: machineIds,
        sendAll: sendAll,
      }),
    );
  }

  getUser(merchantId: number, userId: number): Observable<any> {
    return this.http.get(
      "wallet/v1/user/stat/" + userId + "/merchant/" + merchantId,
    );
  }

  getWalletStats(walletId: number): Observable<any> {
    return this.http.get("wallet/v1/user/stat/wallet/" + walletId);
  }

  getPurchasesByProductLine(walletId: number): Observable<any> {
    return this.http.get("wallet/v1/user/stat/sale/products/" + walletId);
  }

  getPurchasesByHour(walletId: number): Observable<any> {
    return this.http.get("wallet/v1/user/stat/sale/hour/" + walletId);
  }

  getTypesOfPurchases(walletId: number): Observable<any> {
    return this.http.get("wallet/v1/user/stat/pie/recharges/" + walletId);
  }

  getTypesOfPromotions(walletId: number): Observable<any> {
    return this.http.get("wallet/v1/user/stat/pie/promotions/" + walletId);
  }

  getMachineUsedByPurchases(walletId: number): Observable<any> {
    return this.http.get(
      "wallet/v1/user/stat/pie/machine/purchase/" + walletId,
    );
  }

  getMachineUsedByRecharges(walletId: number): Observable<any> {
    return this.http.get("wallet/v1/user/stat/pie/machine/charge/" + walletId);
  }

  deleteUser(userId) {
    return this.http.delete("wallet/v1/user/" + userId);
  }

  resetPassword(merchantId: number, userId: number) {
    return this.http.put(
      "merchant/v1/merchants/" +
        merchantId +
        "/user/" +
        userId +
        "/password/reset",
      "",
    );
  }

  getMachineInfo(merchantId: number, machineId: number): Observable<any> {
    return this.http.get(
      `merchant/v1/merchants/${merchantId}/info/machine/${machineId}`,
    );
  }

  getWalletInfo(walletId: number): Observable<any> {
    return this.http.get(`wallet/v1/user/summary/wallet/${walletId}`);
  }

  getMachineUserInfo(walletId: number): Observable<any> {
    return this.http.get(`wallet/v1/user/stat/wallet//${walletId}/machine`);
  }

  getAverageUserInfo(walletId: number): Observable<any> {
    return this.http.get(`wallet/v1/user/stat/wallet//${walletId}/averages`);
  }

  getRechargesReport(
    merchantId: number,
    account_id: number,
    parameters: any,
    language: string,
  ) {
    return this.http.get(
      `wallet/v1/merchants/${merchantId}/accounts/${account_id}/transactions/report`,
      parameters,
      language,
      "blob",
    );
  } //parameters.ng2TableFilterParameters

  getMachineGroups(
    merchantId: number,
    page: number,
    size: number,
    dataBag: any,
    filterParams: string,
  ): Observable<any> {
    const http = ServiceLocator.injector.get(HttpService);
    return http
      .get(
        `/merchant/v1/merchants/${merchantId}/machineGroups?page=${page}&size=${size}${filterParams}`,
      )
      .pipe(map((response) => response.machineGroups));
  }

  getMachineGroupsCount(
    merchantId: number,
    dataBag: any,
    filterParams: string = "",
  ): Observable<number> {
    const http = ServiceLocator.injector.get(HttpService);
    return http.get(
      `/merchant/v1/merchants/${merchantId}/machineGroups/count${filterParams}`,
    );
  }

  getMachineGroupsList(
    merchantId: number,
    filterParams?: string,
  ): Observable<any> {
    const http = ServiceLocator.injector.get(HttpService);
    return http
      .get(`/merchant/v1/merchants/${merchantId}/machineGroups?${filterParams}`)
      .pipe(map((response) => response.machineGroups));
  }
}

export class UsersResponse {
  user!: User[];
}
